/**
 * Media Library — admin-managed shared asset folder for logos, banners,
 * PDFs and other files that need to be linked from templates and
 * portal documents.
 *
 * Backed by /api/admin/media/* (S3 storage). Public files can be pasted
 * as `<img src>` inside email templates using the stable public URL
 * shown in the "Copy URL" button.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Upload, Copy, Trash2, Search, ImageIcon, FileText, ExternalLink, Edit2, X, Tag as TagIcon, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const AdminMediaPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [defaultFolders, setDefaultFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [s3Configured, setS3Configured] = useState(true);
  const [maxBytes, setMaxBytes] = useState(25 * 1024 * 1024);
  const [allowedMimes, setAllowedMimes] = useState([]);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', folder: 'Other', tags: '', visibility: 'public' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [detail, setDetail] = useState(null); // { ..., usage: [], presigned_url }

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedFolder) params.append('folder', selectedFolder);
      if (search) params.append('q', search);
      const r = await axios.get(`${API_URL}/api/admin/media?${params}`, { headers });
      setItems(r.data.items || []);
      setFolders(r.data.folders || []);
      setDefaultFolders(r.data.default_folders || []);
      setS3Configured(r.data.s3_configured);
      setMaxBytes(r.data.max_bytes || (25 * 1024 * 1024));
      setAllowedMimes(r.data.allowed_mimes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Choose a file first'); return; }
    if (uploadFile.size > maxBytes) {
      toast.error(`File too large — max ${formatBytes(maxBytes)}`);
      return;
    }
    const fd = new FormData();
    fd.append('file', uploadFile);
    if (uploadForm.name) fd.append('name', uploadForm.name);
    if (uploadForm.description) fd.append('description', uploadForm.description);
    fd.append('folder', uploadForm.folder || 'Other');
    fd.append('tags', uploadForm.tags || '');
    fd.append('visibility', uploadForm.visibility || 'public');
    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/admin/media/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ name: '', description: '', folder: 'Other', tags: '', visibility: 'public' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const publicUrlFor = (m) => `${API_URL}/api/media/public/${m.id}`;

  const copyUrl = (m) => {
    if (m.visibility !== 'public') {
      toast.warning('This is a private file — use the "Get presigned URL" action instead.');
      return;
    }
    navigator.clipboard.writeText(publicUrlFor(m));
    toast.success('Public URL copied');
  };

  const copyImgTag = (m) => {
    const tag = `<img src="${publicUrlFor(m)}" alt="${m.name.replace(/"/g, '&quot;')}" />`;
    navigator.clipboard.writeText(tag);
    toast.success('<img> tag copied — paste into your template body');
  };

  const openDetail = async (m) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/media/${m.id}`, { headers });
      setDetail(r.data);
    } catch (e) {
      toast.error('Failed to load media details');
    }
  };

  const startEdit = (m) => {
    setEditing(m);
    setEditForm({
      name: m.name || '',
      description: m.description || '',
      folder: m.folder || 'Other',
      tags: (m.tags || []).join(', '),
      visibility: m.visibility || 'public',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/media/${editing.id}`,
        {
          name: editForm.name,
          description: editForm.description,
          folder: editForm.folder,
          tags: (editForm.tags || '').split(',').map(t => t.trim()).filter(Boolean),
          visibility: editForm.visibility,
        },
        { headers },
      );
      toast.success('Updated');
      setEditing(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? This cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/media/${m.id}`, { headers });
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      else toast.success('Deleted');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const isImage = (m) => (m.content_type || '').startsWith('image/');

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="admin-media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Media Library</h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload logos, images and PDFs once — paste their URLs into emails, statements and templates.
          </p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          disabled={!s3Configured}
          data-testid="media-upload-btn"
        >
          <Upload className="h-4 w-4 mr-2" /> Upload
        </Button>
      </div>

      {!s3Configured && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <strong>S3 storage is not configured on this environment.</strong>
              &nbsp;Uploads are disabled here. Set <code>AWS_ACCESS_KEY_ID</code>, <code>AWS_SECRET_ACCESS_KEY</code>, and <code>AWS_S3_BUCKET</code> in production.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, description or tag…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="media-search-input"
            />
          </div>
          <Select value={selectedFolder || 'all'} onValueChange={v => setSelectedFolder(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-56" data-testid="media-folder-filter">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders ({items.length})</SelectItem>
              {folders.map(([name, count]) => (
                <SelectItem key={name} value={name}>{name} ({count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Grid */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Files ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-sm text-gray-500 py-8">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-12">
              No media files yet. Click <strong>Upload</strong> to add your first logo or PDF.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Preview</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Folder / Tags</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(m => (
                  <TableRow key={m.id} data-testid={`media-row-${m.id}`}>
                    <TableCell>
                      {isImage(m) && m.visibility === 'public' ? (
                        <img
                          src={publicUrlFor(m)}
                          alt={m.name}
                          className="h-12 w-16 object-cover rounded border"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="h-12 w-16 flex items-center justify-center rounded bg-gray-100 border">
                          {isImage(m) ? <ImageIcon className="h-5 w-5 text-gray-400" /> : <FileText className="h-5 w-5 text-gray-400" />}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <button className="text-left hover:text-amber-600" onClick={() => openDetail(m)}>
                        <div className="font-medium">{m.name}</div>
                        {m.description && <div className="text-xs text-gray-500 line-clamp-1">{m.description}</div>}
                        <div className="text-xs text-gray-400 font-mono">{m.original_filename}</div>
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{m.folder}</Badge>
                      {(m.tags || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(m.tags || []).slice(0, 3).map(t => (
                            <span key={t} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                              <TagIcon className="h-3 w-3 inline mr-0.5" />{t}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{formatBytes(m.size)}</TableCell>
                    <TableCell>
                      <Badge className={m.visibility === 'public' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {m.visibility}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {(m.uploaded_at || '').split('T')[0]}<br />
                      <span className="text-gray-400">{m.uploaded_by}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => copyUrl(m)}
                          title="Copy public URL"
                          data-testid={`media-copy-url-${m.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {isImage(m) && m.visibility === 'public' && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => copyImgTag(m)}
                            title="Copy <img> tag"
                            data-testid={`media-copy-img-${m.id}`}
                          >
                            &lt;img&gt;
                          </Button>
                        )}
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => window.open(publicUrlFor(m), '_blank')}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => startEdit(m)}
                          data-testid={`media-edit-${m.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleDelete(m)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`media-delete-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Upload media</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>File</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept={allowedMimes.join(',')}
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm border rounded p-2"
                data-testid="media-file-input"
              />
              <div className="text-xs text-gray-500 mt-1">
                Max {formatBytes(maxBytes)}. Allowed: images (png/jpg/gif/webp/svg) and PDF.
              </div>
              {uploadFile && (
                <div className="text-xs text-gray-600 mt-2">
                  {uploadFile.name} — {formatBytes(uploadFile.size)}
                </div>
              )}
            </div>
            <div>
              <Label>Display name (optional)</Label>
              <Input value={uploadForm.name} onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })} placeholder="Defaults to filename" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={uploadForm.description} onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Folder</Label>
                <Select value={uploadForm.folder} onValueChange={v => setUploadForm({ ...uploadForm, folder: v })}>
                  <SelectTrigger data-testid="media-upload-folder"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {defaultFolders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select value={uploadForm.visibility} onValueChange={v => setUploadForm({ ...uploadForm, visibility: v })}>
                  <SelectTrigger data-testid="media-upload-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public (for &lt;img&gt; in emails)</SelectItem>
                    <SelectItem value="private">Private (auth required)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={uploadForm.tags} onChange={e => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="ptc, header, logo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !uploadFile}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="media-upload-submit"
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit media</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Folder</Label>
                  <Select value={editForm.folder} onValueChange={v => setEditForm({ ...editForm, folder: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {defaultFolders.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select value={editForm.visibility} onValueChange={v => setEditForm({ ...editForm, visibility: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input value={editForm.tags} onChange={e => setEditForm({ ...editForm, tags: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="media-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog (URL + usage) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.name}
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setDetail(null)}><X className="h-4 w-4" /></Button>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail.kind === 'image' && detail.visibility === 'public' && (
                <img src={publicUrlFor(detail)} alt={detail.name} className="max-h-64 object-contain mx-auto border rounded" />
              )}
              <div className="space-y-2 text-sm">
                <div><strong>File key:</strong> <code className="text-xs bg-gray-100 px-1 rounded">{detail.file_key}</code></div>
                <div><strong>Content-Type:</strong> {detail.content_type} — {formatBytes(detail.size)}</div>
                <div><strong>Visibility:</strong> {detail.visibility}</div>
                <div><strong>Uploaded:</strong> {detail.uploaded_at} by {detail.uploaded_by}</div>
                <div>
                  <strong>Public URL:</strong>
                  <div className="mt-1 flex gap-2 items-center">
                    <code className="flex-1 text-xs bg-gray-100 px-2 py-1 rounded break-all">{publicUrlFor(detail)}</code>
                    <Button size="sm" variant="outline" onClick={() => copyUrl(detail)}><Copy className="h-3 w-3" /></Button>
                  </div>
                </div>
                {detail.presigned_url && (
                  <div>
                    <strong>Presigned URL (10 min):</strong>
                    <div className="mt-1 flex gap-2 items-center">
                      <code className="flex-1 text-xs bg-gray-100 px-2 py-1 rounded break-all truncate">{detail.presigned_url}</code>
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(detail.presigned_url); toast.success('Presigned URL copied'); }}><Copy className="h-3 w-3" /></Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="font-semibold text-sm mb-2">Usage ({(detail.usage || []).length})</div>
                {(detail.usage || []).length === 0 ? (
                  <div className="text-xs text-gray-500">Not referenced by any template yet.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Template name</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.usage || []).map(u => (
                        <TableRow key={u.id}>
                          <TableCell>{u.name}</TableCell>
                          <TableCell><Badge variant="outline">{u.template_type}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMediaPage;
